/*
 *  This document is the CONFIDENTIAL PROPERTY of Corero Network Security, Inc.
 *  Copyright (C) 2024-2025 by Corero Network Security, Inc. All rights reserved.
 *  Notice of copyright on this document does not constitute publication.
 */

import { isNilOrEmpty } from '../../../utils/commonUtils';

const DEFAULT_FIELD_SIZE = {
  input: { xs: 8 },
  dropdown: { xs: 8 },
  radioGroup: { xs: 6 },
  timePicker: { xs: 2 },
  keyValue: { xs: 6 },
 };
const DEFAULT_LABEL_SIZE = {   
  input: { xs: 4 },
  dropdown: { xs: 4 },
  radioGroup: { xs: 3 },
  timePicker: { xs: 5 },
  keyValue: { xs: 4 },
};

const generateSizeOptions = (targetSizes, otherSizes, target, type) => {
  if (isNilOrEmpty(targetSizes)) {
    if (isNilOrEmpty(otherSizes)) {
      return target === 'field' ? DEFAULT_FIELD_SIZE[type] : DEFAULT_LABEL_SIZE[type];
    }

    return Object.keys(otherSizes).reduce((acc, curr) => ({
      ...acc,
      [curr]: 12 - otherSizes[curr],
    }), {});
  }

  return targetSizes;
}

/**
 * Generate field and label sizes for form field columns. If no sizes are provided return the default size for the field type.
 * If only the field size or the label size is provided for each breakpoint, find the size of the other component by
 * removing the provided size from 12.
 * @param {object} fieldSizes - the size of the field at various breakpoints
 * @param {object} labelSizes - the size of the label at various breakpoints
 * @param {string} type - the form component type to retrieve the default values for
 * @returns {
 *   label: ({
*       sm?: number,
*       md?: number,
*       lg?: number,
*       xl?: number,
*       xxl?: number,
*   }),
*   field: ({
*       sm?: number,
*       md?: number,
*       lg?: number,
*       xl?: number,
*       xxl?: number,
*   }),
* }
 */
export const getSizeOptions = (fieldSizes, labelSizes, type) => ({
    field: generateSizeOptions(fieldSizes, labelSizes, 'field', type),
    label: generateSizeOptions(labelSizes, fieldSizes, 'label', type),
});

/**
 * Finds the first tab that contains a validation error
 *
 * @param validation the validation config (usually the {@code $validation} object)
 * @param tabConfig  the tab configuration
 * @param activeTab  the active tab
 * @returns {string} the tab key of the first tab that contains a validation error, or the active tab key if no tab has
 *                   an error
 */
export const findFirstInvalidTab = (validation, tabConfig, activeTab) => {
  // Map to preserve key order
  const errorByTab = new Map(tabConfig.map(({ tabKey }) => ([tabKey, false])));

  // Find which tabs have error
  for (const field in validation) {
    const fieldValidation = validation[field];
    if (fieldValidation?.error?.reason) {
      const errorTabKey = tabConfig.find(({ tabFields }) => tabFields.includes(field))?.tabKey;
      errorByTab.set(errorTabKey, true);
    }
  }

  // Stay in current tab if there are errors there
  if (errorByTab.get(activeTab)) {
    return activeTab;
  }

  // Otherwise go to first tab that contains errors
  for (const [tabKey, hasError] of errorByTab) {
    if (hasError) {
      return tabKey;
    }
  }
}